// Partner rollout — canonical data for the chairman 1-pager.
//
// Sourced from three documents reviewed in the 29 May 2026 ManCo:
//   - Bvndle Redemption_Partners_Report 2026.docx (11 partner categories +
//     priority/standard merchants + YTD redemption Feb-May 2026)
//   - Bvndle_Semiloore_Product Priority.xlsx (sprint product items by owner
//     and status)
//   - TASKS TRACKER.xlsx (11 ManCo deliverables for the chairman review)
//
// The taxonomy on this page is the 11 partner-report categories, in the
// same order they appear in the report. The existing data-anchored
// categories.ts is left untouched — it powers the operational S/D
// surfaces (which still anchor on coinsLedger narrations); the chairman
// surface anchors on what the team is actively delivering.
//
// Refresh cadence: whenever the partners report / trackers move, this
// file gets refreshed. Next planned update: when the S/D surfaces are
// re-aligned to this taxonomy (Cut 5.1).

// ---------------------------------------------------------------------------
// Status icons — match the partners-report legend exactly so the chairman
// sees the same symbols he sees in the .docx.
// ---------------------------------------------------------------------------

export type MerchantStatus =
  | "onboarded" //          ● Fully onboarded, live on the Bvndle platform
  | "integrating" //        ◕ Integrating
  | "mgmt-alignment" //     ◑ Mgmt. alignment
  | "reviewing-sla" //      ◐ Reviewing SLA
  | "value-prop" //         ○ Value-proposition phase
  | "prospect"; //          ◌ Prospect, not yet engaged

export const STATUS_ICON: Record<MerchantStatus, string> = {
  "onboarded": "●",
  "integrating": "◕",
  "mgmt-alignment": "◑",
  "reviewing-sla": "◐",
  "value-prop": "○",
  "prospect": "◌",
};

export const STATUS_LABEL: Record<MerchantStatus, string> = {
  "onboarded": "Onboarded",
  "integrating": "Integrating",
  "mgmt-alignment": "Mgmt. Alignment",
  "reviewing-sla": "Reviewing SLA",
  "value-prop": "Value Prop",
  "prospect": "Prospect",
};

export interface Merchant {
  name: string;
  status: MerchantStatus;
  reach: string;
  /** Total physical branches, or null for online-only/unknown. */
  totalBranches: number | null;
  /** Branches actively live on Bvndle. */
  onboardedBranches: number | null;
  /** YTD redemption (Feb-May 2026) in coins, or null if no activity. */
  ytdRedemption?: number | null;
  /** True for merchants from the "Standard Merchants" supplementary list. */
  standard?: boolean;
}

export type ProductStatus =
  | "live" // shipped & operating
  | "testing"
  | "in-design"
  | "under-review"
  | "pending"
  | "blocked"
  | "stalled"
  | "not-started"
  | "completed";

export interface ProductItem {
  title: string;
  type: string;
  owner: string;
  status: ProductStatus;
  notes?: string;
  sprint?: string;
}

export interface TrackerTask {
  number: number;
  title: string;
  owner: string;
  dueDate: string;
  status: "done" | "wip" | "pending" | "blocked";
  comments?: string;
}

export interface PartnerCategory {
  code: string;
  label: string;
  description: string;
  /** True for the PRIMARY anchor category (Supermarkets per the report). */
  primary?: boolean;
  /** Lead owner per the trackers — best-guess where the source is silent. */
  owner: string;
  /** Priority + standard merchants combined, priority first. */
  merchants: Merchant[];
  /** Product-priority items the team has mapped to this category. */
  productItems: ProductItem[];
  /** ManCo tracker tasks that touch this category. */
  trackerTasks: TrackerTask[];
  /** One-line blocker summary if anything's stuck. Empty string = no blocker. */
  blockers: string;
  /** One-line "what we're doing about it" — short, chairman-friendly. */
  doing: string;
}

// ---------------------------------------------------------------------------
// Shared product items + tracker tasks that touch every category (or none in
// particular). Referenced by the categories below + the platform footer.
// ---------------------------------------------------------------------------

export const PLATFORM_PRODUCT_ITEMS: ProductItem[] = [
  { title: "App Revamp", type: "Redesign", owner: "Semiloore", status: "pending" },
  { title: "Partner onboarding flow redesign", type: "Redesign", owner: "PSL + Ife", status: "stalled" },
  { title: "Partner-led view for Marketplace", type: "Redesign", owner: "Ikechukwu", status: "in-design" },
  { title: "Onboarding nudges", type: "New Feature", owner: "Semiloore + Ife", status: "testing" },
  { title: "Customer.io integration", type: "Integration", owner: "Semiloore", status: "live", notes: "Phase 1 live, phase 2 pending" },
  { title: "Gamification Landing Page", type: "New Feature", owner: "Ife", status: "not-started" },
  { title: "Update Hot Bvndles section logic", type: "Improvement", owner: "Semiloore", status: "not-started" },
  { title: "Service-charge / transaction-fee copy change", type: "Improvement", owner: "IK + Semiloore", status: "not-started" },
];

export const PLATFORM_TRACKER_TASKS: TrackerTask[] = [
  { number: 1, title: "UBA / MTN readiness update", owner: "Ada", dueDate: "22 May", status: "done", comments: "Report tabled at this meeting" },
  { number: 2, title: "MTN Vending contract updates", owner: "Chibuzor", dueDate: "1 Jun", status: "pending", comments: "Final S&D-Chief approval pending; Tobe close-out tomorrow" },
  { number: 4, title: "App revamp", owner: "Semiloore", dueDate: "27 May", status: "pending" },
  { number: 9, title: "MTN Pentagon licence", owner: "Chibuzor", dueDate: "27 May", status: "pending", comments: "Licence due Oct 2026; commence ops via Pentagon vendors pending" },
  { number: 10, title: "Landing pages & website", owner: "Ifedolapo", dueDate: "27 May", status: "pending" },
  { number: 11, title: "Merchant database clean-up", owner: "Ufuoma", dueDate: "27 May", status: "done" },
];

// ---------------------------------------------------------------------------
// 11 partner categories — in partners-report order.
//
// "Value indicators" per Nonso's framing (cf. handwritten notes from the
// 29 May standup) are derived from the merchant list: # onboarded /
// # in pipeline, plus YTD redemption where available.
// ---------------------------------------------------------------------------

export const PARTNER_CATEGORIES: PartnerCategory[] = [
  // 1 — RETAIL (SUPERMARKETS) ----------------------------------------------
  {
    code: "supermarkets",
    label: "Retail · Supermarkets",
    description: "PRIMARY — everyday essential spending anchor",
    primary: true,
    owner: "Williams · Ufuoma (high-redemption status)",
    merchants: [
      { name: "Jendol Superstores", status: "onboarded", reach: "Regional", totalBranches: 12, onboardedBranches: 10, ytdRedemption: 67208 },
      { name: "Market Square", status: "onboarded", reach: "National", totalBranches: 38, onboardedBranches: 2, ytdRedemption: 1042 },
      { name: "Trolleys Supermarket", status: "onboarded", reach: "National", totalBranches: 1, onboardedBranches: 1, ytdRedemption: 7653 },
      { name: "Legend Supermarket", status: "reviewing-sla", reach: "Lagos", totalBranches: 1, onboardedBranches: null },
      { name: "Justrite", status: "value-prop", reach: "Regional", totalBranches: 37, onboardedBranches: null },
      { name: "Home Affairs", status: "value-prop", reach: "Regional", totalBranches: 4, onboardedBranches: null },
      { name: "Grandsquare", status: "value-prop", reach: "National", totalBranches: 4, onboardedBranches: null },
      { name: "FoodCo", status: "value-prop", reach: "Regional", totalBranches: 23, onboardedBranches: null },
    ],
    productItems: [],
    trackerTasks: [
      { number: 3, title: "Status of high-redemption partners", owner: "Ufuoma", dueDate: "27 May", status: "done", comments: "Summary tabled at this meeting" },
    ],
    blockers: "—",
    doing: "Three priority anchors live (Jendol, Market Square, Trolleys). Legend SLA in review; four value-prop merchants in the queue. Jendol drives 34% of YTD redemption — protect and scale.",
  },

  // 2 — AUTOMOTIVE / MOBILITY ----------------------------------------------
  {
    code: "automotive",
    label: "Automotive · Mobility",
    description: "Fuel & transport — high-frequency utility",
    owner: "Williams · Ruki (BvndleGo)",
    merchants: [
      { name: "Nepal Fuel Station", status: "onboarded", reach: "National", totalBranches: 35, onboardedBranches: 1, ytdRedemption: 20 },
      { name: "Ardova Plc (Pre-funded)", status: "onboarded", reach: "National", totalBranches: 450, onboardedBranches: 3 },
      { name: "BvndleGo", status: "onboarded", reach: "Regional", totalBranches: null, onboardedBranches: null, ytdRedemption: 0 },
      { name: "Total Energies Filling Station", status: "value-prop", reach: "National", totalBranches: 200, onboardedBranches: null },
      { name: "Eterna Filling Station", status: "prospect", reach: "National", totalBranches: 250, onboardedBranches: null },
      { name: "Edryv", status: "integrating", reach: "Nationwide", totalBranches: null, onboardedBranches: null },
    ],
    productItems: [
      { title: "Bvndle Go", type: "New Feature", owner: "Ruki", status: "not-started", notes: "Key part of next sprint" },
      { title: "New Hot Bvndle — Fuel / Filling Stations", type: "New Feature", owner: "Nonso / Semiloore", status: "not-started" },
    ],
    trackerTasks: [],
    blockers: "BvndleGo product not yet started — limits transport redemption. Pentagon licence dependency for fuel-merchant scale.",
    doing: "Three anchors live (Nepal, Ardova, BvndleGo placeholder). Edryv integrating. Fuel-station Hot-Bvndle slot queued in product backlog; Bvndle Go is next-sprint priority.",
  },

  // 3 — HEALTH & WELLNESS --------------------------------------------------
  {
    code: "health-wellness",
    label: "Health & Wellness",
    description: "Fitness, pharmacy, personal care services",
    owner: "Seye (Medplus LaaS) · Williams",
    merchants: [
      { name: "i-Fitness", status: "onboarded", reach: "National", totalBranches: 30, onboardedBranches: 29, ytdRedemption: 4288 },
      { name: "Medplus", status: "mgmt-alignment", reach: "National", totalBranches: 110, onboardedBranches: null },
      { name: "The Ace Beauty Bar", status: "onboarded", reach: "Lagos", totalBranches: 1, onboardedBranches: 1, standard: true },
    ],
    productItems: [],
    trackerTasks: [],
    blockers: "Medplus stuck at mgmt-alignment — pharmacy depth gated on a single decision.",
    doing: "i-Fitness essentially fully onboarded (29/30 branches). Closing the Medplus mgmt-alignment to unlock 110 pharmacy branches.",
  },

  // 4 — DINING / RESTAURANTS -----------------------------------------------
  {
    code: "dining",
    label: "Dining · Restaurants",
    description: "Food & beverage experiences",
    owner: "Williams · CX + PSL (Glovo)",
    merchants: [
      { name: "Yellow Chilli", status: "onboarded", reach: "Lagos", totalBranches: 2, onboardedBranches: 2, ytdRedemption: 40 },
      { name: "El Padrino", status: "onboarded", reach: "Lagos", totalBranches: 2, onboardedBranches: 2, ytdRedemption: 24420 },
      { name: "Burger King", status: "reviewing-sla", reach: "National", totalBranches: 16, onboardedBranches: null },
      { name: "Olive Family / Panarottis", status: "mgmt-alignment", reach: "National", totalBranches: 12, onboardedBranches: null },
      { name: "Tastee Fried Chicken", status: "value-prop", reach: "Regional", totalBranches: 14, onboardedBranches: null },
      { name: "Chicken Republic", status: "value-prop", reach: "National", totalBranches: 200, onboardedBranches: null },
      { name: "Kilimanjaro", status: "value-prop", reach: "National", totalBranches: 90, onboardedBranches: null },
      { name: "Debonairs Pizza", status: "value-prop", reach: "National", totalBranches: 9, onboardedBranches: null },
      { name: "Item 7", status: "prospect", reach: "Regional", totalBranches: 18, onboardedBranches: null },
      { name: "Ofada Hut", status: "onboarded", reach: "Lagos", totalBranches: 2, onboardedBranches: 2, ytdRedemption: 52338, standard: true },
      { name: "Yoko Foods", status: "onboarded", reach: "Lagos", totalBranches: 2, onboardedBranches: 2, standard: true },
      { name: "The Beanry", status: "onboarded", reach: "Lagos", totalBranches: 1, onboardedBranches: 1, standard: true },
      { name: "Rubels and Angels", status: "value-prop", reach: "Lagos", totalBranches: 5, onboardedBranches: null, standard: true },
    ],
    productItems: [
      { title: "Glovo Integration", type: "Integration", owner: "CX + PSL", status: "under-review", notes: "Team syncing still ongoing" },
    ],
    trackerTasks: [],
    blockers: "Glovo integration under review — delivery rail not yet on Bvndle.",
    doing: "Two priority + three standard merchants live; El Padrino (₦24k YTD) and Ofada Hut (₦52k YTD) are the heaviest redeemers across the whole network. Burger King SLA + Panarottis proposal in the queue.",
  },

  // 5 — BEAUTY & PERSONAL CARE ---------------------------------------------
  {
    code: "beauty",
    label: "Beauty & Personal Care",
    description: "Lifestyle grooming & beauty retail",
    owner: "Williams",
    merchants: [
      { name: "Dang Lifestyle", status: "onboarded", reach: "National", totalBranches: 7, onboardedBranches: 7, ytdRedemption: 99 },
      { name: "Massage by Camel", status: "onboarded", reach: "Lagos", totalBranches: 1, onboardedBranches: 1 },
      { name: "The Montaigne Place", status: "reviewing-sla", reach: "National", totalBranches: 36, onboardedBranches: null },
      { name: "Ginger", status: "reviewing-sla", reach: "National", totalBranches: null, onboardedBranches: null },
      { name: "Oriki Spa", status: "value-prop", reach: "West Africa", totalBranches: 12, onboardedBranches: null },
      { name: "Hair House Nigeria", status: "prospect", reach: "National", totalBranches: 1, onboardedBranches: null },
      { name: "CNM Essentials", status: "onboarded", reach: "National", totalBranches: 1, onboardedBranches: 1, standard: true },
      { name: "The Fragrance Lagos", status: "onboarded", reach: "National", totalBranches: 1, onboardedBranches: 1, ytdRedemption: 7205, standard: true },
      { name: "Ginger Me", status: "onboarded", reach: "National", totalBranches: null, onboardedBranches: null, standard: true },
    ],
    productItems: [],
    trackerTasks: [],
    blockers: "—",
    doing: "Two priority + three standard merchants live. The Montaigne Place + Ginger both at SLA stage — closing either would unlock 36+ branches.",
  },

  // 6 — E-COMMERCE / DIGITAL SERVICES --------------------------------------
  {
    code: "ecommerce",
    label: "E-Commerce · Digital Services",
    description: "Online platforms with broad reach",
    owner: "Williams · PSL (LaaS)",
    merchants: [
      { name: "Pricepally", status: "onboarded", reach: "Global", totalBranches: null, onboardedBranches: null, ytdRedemption: 220 },
      { name: "Garamart", status: "onboarded", reach: "National", totalBranches: null, onboardedBranches: null, ytdRedemption: 3909 },
      { name: "Konga", status: "onboarded", reach: "National", totalBranches: null, onboardedBranches: null },
    ],
    productItems: [
      { title: "VBaaS integration", type: "Integration", owner: "PSL", status: "live" },
    ],
    trackerTasks: [],
    blockers: "—",
    doing: "Three priority anchors all live. Konga onboarded but no redemption volume yet — next move is driving traffic via in-app placement.",
  },

  // 7 — TECHNOLOGY & ELECTRONICS -------------------------------------------
  {
    code: "technology",
    label: "Technology & Electronics",
    description: "Consumer tech & electronics retail",
    owner: "Williams",
    merchants: [
      { name: "Festajei Electronics", status: "onboarded", reach: "Regional", totalBranches: 3, onboardedBranches: 3, ytdRedemption: 30 },
      { name: "SIMS", status: "prospect", reach: "National", totalBranches: 18, onboardedBranches: null },
      { name: "Samsung — MGS", status: "prospect", reach: "National", totalBranches: 30, onboardedBranches: null },
      { name: "Slot", status: "prospect", reach: "National", totalBranches: 80, onboardedBranches: null },
    ],
    productItems: [],
    trackerTasks: [],
    blockers: "Only one anchor onboarded; three high-reach prospects (Samsung, Slot, SIMS) untouched.",
    doing: "Festajei live as proof-of-concept. Need first outreach to Samsung/Slot — both ≥30 branches, would step-change category coverage.",
  },

  // 8 — HOSPITALITY & TRAVEL -----------------------------------------------
  {
    code: "hospitality",
    label: "Hospitality & Travel",
    description: "Hotels, serviced apartments & travel",
    owner: "Williams · Ayo (Amber Hospitality deck in review)",
    merchants: [
      { name: "Boardroom Apartment", status: "onboarded", reach: "Regional", totalBranches: 1, onboardedBranches: 1, ytdRedemption: 3105 },
      { name: "Delborough", status: "value-prop", reach: "Regional", totalBranches: 1, onboardedBranches: null },
      { name: "Presken Hotel", status: "value-prop", reach: "National", totalBranches: 18, onboardedBranches: null },
      { name: "Hilton", status: "prospect", reach: "Regional", totalBranches: 2, onboardedBranches: null },
      { name: "Eko Hotel", status: "prospect", reach: "Regional", totalBranches: 1, onboardedBranches: null },
      { name: "355 Lounges / Hotels", status: "prospect", reach: "Regional", totalBranches: null, onboardedBranches: null },
    ],
    productItems: [],
    trackerTasks: [],
    blockers: "Only one onboarded; pipeline is thin (mostly Value Prop / Prospect).",
    doing: "Boardroom Apt is a live single-anchor (₦3.1k YTD). Amber Hospitality x Bvndle proposal in review (Ayo / Williams). Closing Delborough + Presken would round out the priority three.",
  },

  // 9 — FASHION & ACCESSORIES ----------------------------------------------
  {
    code: "fashion",
    label: "Fashion & Accessories",
    description: "Apparel & lifestyle fashion retail",
    owner: "Williams",
    merchants: [
      { name: "Ruff n Tumble", status: "value-prop", reach: "National", totalBranches: null, onboardedBranches: null },
      { name: "Twice As Nice", status: "value-prop", reach: "Regional", totalBranches: null, onboardedBranches: null },
      { name: "Essenza", status: "prospect", reach: "Regional", totalBranches: null, onboardedBranches: null },
      { name: "4ey Fashion", status: "onboarded", reach: "National", totalBranches: 1, onboardedBranches: 1, ytdRedemption: 28315, standard: true },
    ],
    productItems: [],
    trackerTasks: [],
    blockers: "Zero priority merchants onboarded — only a single standard merchant (4ey) live, though it punches above its weight at ₦28k YTD.",
    doing: "4ey Fashion is the surprise volume driver (3rd highest single-merchant YTD across the whole network). Use it as proof to close Ruff n Tumble + Twice As Nice from Value Prop.",
  },

  // 10 — ENTERTAINMENT & EXPERIENCES ---------------------------------------
  {
    code: "entertainment",
    label: "Entertainment & Experiences",
    description: "Cinema, leisure & social experiences",
    owner: "Williams · Seye (Filmhouse)",
    merchants: [
      { name: "Carven", status: "onboarded", reach: "Regional", totalBranches: null, onboardedBranches: null, ytdRedemption: 1001 },
      { name: "Breather NG", status: "onboarded", reach: "Regional", totalBranches: 1, onboardedBranches: 1 },
      { name: "Film House", status: "integrating", reach: "National", totalBranches: 12, onboardedBranches: null },
      { name: "Rufus and Bees", status: "value-prop", reach: "Regional", totalBranches: 1, onboardedBranches: null },
      { name: "Ebonylife Place", status: "prospect", reach: "Regional", totalBranches: 1, onboardedBranches: null },
    ],
    productItems: [
      { title: "Filmhouse Integration", type: "Integration", owner: "Seye", status: "blocked", sprint: "11 May" },
    ],
    trackerTasks: [],
    blockers: "Filmhouse integration BLOCKED — 12-branch national chain stuck mid-flight, blocking the largest entertainment unlock.",
    doing: "Two anchors live (Carven, Breather). Unblocking Filmhouse is the priority — would triple category branch coverage on its own.",
  },

  // 11 — GIFTING -----------------------------------------------------------
  {
    code: "gifting",
    label: "Gifting",
    description: "Flowers, gifts & occasion retail",
    owner: "Williams (Bvndle Gift Cards strategy)",
    merchants: [
      { name: "Regal Flowers", status: "onboarded", reach: "National", totalBranches: 2, onboardedBranches: 2, ytdRedemption: 10 },
      { name: "Scribbles", status: "onboarded", reach: "Online", totalBranches: null, onboardedBranches: null, ytdRedemption: 5 },
    ],
    productItems: [
      { title: "Experience Giftcards", type: "New Feature", owner: "Seye", status: "live" },
      { title: "Giftcard Notification + Claim via BvndleBot", type: "New Feature", owner: "PSL + Seye", status: "live" },
    ],
    trackerTasks: [
      { number: 6, title: "Bvndle Gift Cards strategy", owner: "Williams", dueDate: "27 May", status: "wip", comments: "First strategy draft received, under review" },
    ],
    blockers: "—",
    doing: "Two anchors live but minimal volume to date. Williams's gift-card strategy in review will define whether this category gets a dedicated GTM push or stays as an attach to other categories.",
  },
];

// ---------------------------------------------------------------------------
// KPI briefing — the spine of the chairman 1-pager.
//
// Each KPI gets the five-field shape Nonso asked for on the whiteboard:
//   1. NOW                  → current value indicator
//   2. TOMORROW             → target value indicator
//   3. LEAD + TRACK OWNERS  → the named accountabilities
//   4. PARTNER SYNTHESIS    → what the partners report tells us re: this KPI
//   5. BLOCKERS + DOING     → why it's stuck, what's being done
//
// Copy here is strategic — it's the chairman's view, refreshed alongside
// the partners report / trackers, not live data. When per-KPI metrics
// become live-queryable, the NOW/TOMORROW strings can flip to bindings.
// ---------------------------------------------------------------------------

export interface KpiTrackOwner {
  /** Person/team name. */
  name: string;
  /** What they own. */
  scope: string;
  /** Status badge. */
  status: "done" | "wip" | "pending" | "blocked" | "live" | "testing" | "not-started";
}

export interface KpiBrief {
  code: string; // "S1", "S2", "S3", "D1", "D2", "D3"
  title: string;
  question: string;
  /** Current state — short, scannable. */
  now: string;
  /** Target — what success looks like. */
  tomorrow: string;
  /** KPI lead (the named owner from kpis.ts, condensed). */
  lead: string;
  /** Track owners from the trackers, nested under this KPI. */
  trackOwners: KpiTrackOwner[];
  /** What the partners report tells us about this KPI specifically. */
  partnerSynthesis: string;
  blockers: string;
  doing: string;
  // ----- Dense-layout fields (post-29-May Chairman briefing refactor) -----
  /** Combined owners string for the dense layout (eg. "Seye · Ikechukwu"). */
  owners: string;
  /** One-sentence actionable insight rendered in bold under OWNERS. The
   * numbers in this string are the load-bearing claims of the briefing —
   * keep them accurate against the latest partners report / 1-pager. */
  actionableInsight: string;
}

export const SUPPLY_KPIS: KpiBrief[] = [
  {
    code: "S1",
    title: "Where issuance happens",
    question: "Where is earning happening — and where do we want it?",
    now: "UBA card + MTN top-up dominate. Earn through merchant network is a thin slice. 23 priority merchants across 11 partner categories are live and capable of issuing earn-at-spend; daily-cadence categories (Supermarkets, Auto, Dining) carry most of that footprint.",
    tomorrow: "Issuance spread across the 11 partner categories so coins are minted where they will also be spent. Target: 33 merchants live (3 per priority category) by EOY; merchant-earn climbs from <5% to 20% of issuance mix.",
    lead: "Seye · Ikechukwu (ecosystem)",
    owners: "Seye · Ikechukwu (ecosystem)",
    actionableInsight:
      "98% of issuance happens at signup. Spreading earn across the 11 partner categories puts coins where they will also be spent.",
    trackOwners: [
      { name: "Williams", scope: "Merchant onboarding lead", status: "wip" },
      { name: "Ada", scope: "UBA / MTN readiness", status: "done" },
      { name: "Chibuzor", scope: "MTN Vending contract", status: "pending" },
      { name: "Chibuzor", scope: "MTN Pentagon licence (Oct 2026)", status: "pending" },
    ],
    partnerSynthesis:
      "11 categories opened. Supermarkets 3/8 onboarded (Jendol, Market Square, Trolleys). Auto 3/6 (Nepal, Ardova, BvndleGo). Dining 5/13. E-commerce 3/3 (Pricepally, Garamart, Konga). Health 1/2 (i-Fitness, Medplus stuck at mgmt-alignment). Hospitality 1/6 — thin. Tech 1/4, Fashion 0/3 priority — both need first anchor outreach.",
    blockers:
      "MTN Pentagon licence (Oct 2026) gates fuel-merchant scale. Medplus stuck at mgmt-alignment blocks pharmacy depth.",
    doing:
      "Williams driving next-batch onboarding (Burger King SLA, Olive/Panarottis proposal, Filmhouse unblock). Bvndle Go in next sprint expands rideshare earn. Fuel-station Hot-Bvndle slot queued (Nonso/Semiloore).",
  },
  {
    code: "S2",
    title: "Earn frequency",
    question: "Are users earning often enough — does it add up to something useful?",
    now: "Earn cadence weighted to monthly events (top-ups, signup). Daily-cadence earn-at-spend exists in the onboarded retail network (Jendol, Trolleys, Ofada Hut) but rarely triggered — UX doesn't surface coin-earn at point of merchant spend.",
    tomorrow: "Each onboarded merchant becomes a recurring earn moment. Target: at-least-weekly earn event for active users, with fiat-useful balance (₦1k+ equivalent) reached within first month.",
    lead: "Ufuoma · Semilore (Akoni) on data",
    owners: "Ufuoma · Semilore (Akoni) on data",
    actionableInsight:
      "Daily-cadence merchants are onboarded but the customer journey doesn't surface earn-at-spend — El Padrino's 27% of YTD redemption is matched by 0% visible earn.",
    trackOwners: [
      { name: "Semiloore", scope: "App revamp", status: "pending" },
      { name: "Ife", scope: "Gamification landing page", status: "not-started" },
      { name: "Semiloore", scope: "Hot Bvndles section logic (placement priority)", status: "not-started" },
      { name: "Semiloore + Ife", scope: "Onboarding nudges", status: "testing" },
    ],
    partnerSynthesis:
      "Daily-cadence categories (Supermarkets, Dining, Auto, Beauty) carry most of the onboarded mix — the right shape for habitual earn. But customer journey doesn't yet surface 'you earned X coins at this merchant.' Jendol's redemption volume (34% of YTD) is one-sided — earn at Jendol is invisible.",
    blockers: "App revamp pending. Gamification + Hot Bvndles logic not started — limits visibility of earn at merchant moments.",
    doing:
      "Onboarding nudges in testing. App revamp queued. Hot Bvndles repriotisation logic to surface high-cadence-merchant earn moments.",
  },
  {
    code: "S3",
    title: "Cost of issuance",
    question: "What's it costing us — and who's funding it?",
    now: "Issuance funded mostly by Bvndle. Ardova Plc is the proof-of-concept for pre-funded consignment — partner deposits the float, Bvndle mints against it. One merchant doing the model.",
    tomorrow: "Consigned-funded model on every priority partner by category — fee-defense + signup-bonus stays Bvndle-funded; merchant earn becomes consigned. Target: 50%+ of merchant-channel issuance consigned.",
    lead: "Kabelo · Ikechukwu (commercial)",
    owners: "Kabelo · Ikechukwu (commercial)",
    actionableInsight:
      "Ardova is the lone consigned anchor. Cost-of-issuance won't bend until that pre-funded model is replicated across the priority categories.",
    trackOwners: [
      { name: "Kabelo · Adaobi", scope: "Ecosystem partner value proposition", status: "pending" },
      { name: "Kabelo", scope: "Single source KYC workflow", status: "wip" },
    ],
    partnerSynthesis:
      "Ardova Plc tagged 'Pre-funded' in the partners report — only consigned anchor identified. Rest of the network is on Bvndle-funded issuance. The cost-of-issuance question won't bend until the Ardova model is replicated.",
    blockers:
      "Single consigned anchor (Ardova). Most merchants haven't been pitched the consigned model. Ecosystem-partner tech-gap blocks ingesting Bvndle from partners without their own platform.",
    doing:
      "Building the lightweight LaaS surface for partners who can't ingest Bvndle on their own infra (ecosystem-partner workstream). KYC consolidation in peppermint_motherboard, porting to Bvndle Command Centre.",
  },
];

export const DEMAND_KPIS: KpiBrief[] = [
  {
    code: "D1",
    title: "Where redemption happens",
    question: "Where is redemption actually happening?",
    now: "YTD redemption 398,686 coins across 20 active merchants (Jan–May 2026). " +
      "El Padrino leads (27%, 106,524), Jendol second (17%, 67,631), Ofada Hut third (13.5%, 53,990). " +
      "4ey Fashion is the standard-list sleeper hit (28,300 YTD from a single store). May was the strongest month at 164,912 coins.",
    tomorrow: "Redemption distributed across the 11 categories with no single merchant > 20% of total. Target: ≥3 onboarded merchants per priority category; YTD redemption ≥₦1M by EOY.",
    lead: "Ufuoma · Williams (merchants), Chibuzor (Yard)",
    owners: "Ufuoma · Williams (merchants), Chibuzor (Yard)",
    actionableInsight:
      "76% of YTD redemption lives in Dining + Supermarkets + Entertainment. Filmhouse unblock + Auto activation would broaden the base from 3 categories carrying the volume to 8.",
    trackOwners: [
      { name: "Ufuoma", scope: "Status of high-redemption partners", status: "done" },
      { name: "Williams", scope: "Bvndle Gift Cards strategy", status: "wip" },
      { name: "Seye", scope: "Filmhouse integration (12-branch unlock)", status: "blocked" },
      { name: "Ruki", scope: "BvndleGo product", status: "not-started" },
      { name: "CX + PSL", scope: "Glovo integration", status: "pending" },
    ],
    partnerSynthesis:
      "Supermarkets + Dining dominate. Health (i-Fitness Apr-only ₦4.3k), Beauty (Dang Lifestyle modest), Hospitality (Boardroom ₦3.1k only), E-commerce (Garamart ₦3.9k). Entertainment stuck at 2 anchors because Filmhouse integration is blocked — that one unblock would triple category branch coverage. Fashion has zero priority merchants live; 4ey (standard list) outperforms the entire priority pipeline.",
    blockers:
      "Filmhouse integration BLOCKED. Glovo integration UNDER REVIEW. BvndleGo product NOT STARTED — caps transport redemption. Medplus mgmt-alignment caps pharmacy depth.",
    doing:
      "Williams' gift-card strategy under review. Filmhouse unblock prioritised. Glovo integration team-sync ongoing. Fuel-station Hot-Bvndle slot queued (would activate Ardova/Nepal redemption).",
  },
  {
    code: "D2",
    title: "Repeat-redemption habit",
    question: "Are users redeeming often enough — does it form a habit?",
    now: "Per-merchant cohort cuts not yet broken out. Ofada Hut's Feb→May pattern (2.8k → 31.9k → 11.7k → 7.6k) is the cleanest decay signal in the dataset. i-Fitness is a one-month spike (Apr only). Single-time redemption likely dominates.",
    tomorrow: "14d-return ≥ 30% per category. Habit-forming merchants (supermarkets, dining, transport) above 50% same-month repeat. Activation-trap merchants (one-time gift-card redemptions) identified and de-prioritised.",
    lead: "Ibinye · Ruki (BvndleGO redemption)",
    owners: "Ibinye · Ruki (BvndleGO redemption)",
    actionableInsight:
      "Habit-cohort cuts not yet broken out per merchant. Ofada Hut's 32k → 8k decay (Mar → May) is the first retention-strategy gap to close.",
    trackOwners: [
      { name: "Semiloore + Ife", scope: "Onboarding nudges", status: "testing" },
      { name: "Ibinye", scope: "2% Bvndle Card cash-back", status: "live" },
      { name: "Ibinye", scope: "Auto-commission held from merchants", status: "pending" },
      { name: "Semiloore", scope: "App revamp", status: "pending" },
    ],
    partnerSynthesis:
      "Daily-cadence merchants (Supermarkets, Dining) are where habit-formation lives. Jendol's volume + branch coverage suggests it could become the habit anchor. Ofada Hut's decay pattern suggests retention strategy gap. Hospitality + Fashion are occasion-cadence — high value per redemption, low frequency.",
    blockers: "Per-cohort retention data not yet broken out per merchant. App revamp pending. Auto-commission-hold mechanism not live.",
    doing:
      "Onboarding nudges in testing — should lift first-redemption conversion. 2% Bvndle Card cash-back live — reinforces card-cadence habit. App revamp will surface merchant earn / redemption history per user.",
  },
  {
    code: "D3",
    title: "Settlement velocity",
    question: "How fast does each channel settle — what does latency cost us?",
    now: "Settlement rail varies by category — instant for card (UBA), T+1 for POS retail (Supermarkets), pre-funded for Auto (Ardova), manual reconciliation for hospitality + bespoke partners. No single source-of-truth dashboard for rail health.",
    tomorrow: "Instant settlement for ≥70% of redemption volume. Category-level latency dashboards live. Auto-commission-hold mechanism live so merchant float is automatically reserved per redemption.",
    lead: "Kabelo (settlement rails)",
    owners: "Kabelo (settlement rails)",
    actionableInsight:
      "Card is instant, e-commerce is rail-ready, retail is T+1, hospitality is fully bespoke. Auto-commission-hold is the lever to compress the manual tail.",
    trackOwners: [
      { name: "PSL", scope: "VBaaS integration", status: "live" },
      { name: "Ibinye", scope: "Automated % commission held from merchant accounts", status: "pending" },
      { name: "Modupe + Semiloore", scope: "Mixpanel events", status: "live" },
    ],
    partnerSynthesis:
      "Pre-funded model (Ardova) is the fastest settlement story — float lives with Bvndle, no settlement step. Onboarded e-commerce (Pricepally, Garamart, Konga) is online-rail-capable. Standard POS retail (Jendol etc.) is T+1 or manual. Hospitality is fully bespoke today.",
    blockers: "Auto-commission-hold mechanism not live — manual reconciliation required for non-pre-funded merchants. No settlement-velocity dashboard per category.",
    doing:
      "VBaaS integration live. Commission-hold mechanism in development (Ibinye). Mixpanel events live — telemetry foundation for the future per-category rail dashboard.",
  },
];

// ---------------------------------------------------------------------------
// Additional commitments — the two items from Kabelo's handwritten notes
// that don't fit the partner-category surface but need chairman visibility.
// ---------------------------------------------------------------------------

export interface AdditionalCommitment {
  title: string;
  owner: string;
  status: "done" | "wip" | "pending" | "blocked";
  body: string;
}

export const ADDITIONAL_COMMITMENTS: AdditionalCommitment[] = [
  {
    title: "Ecosystem partner value proposition",
    owner: "Kabelo · Adaobi",
    status: "pending",
    body:
      "Value propositions for VFD-ecosystem partners exist. The active work is closing the tech gap for partners who can't ingest Bvndle, mint tags, and report on their own infrastructure — building the lightweight LaaS surface those partners need to participate.",
  },
  {
    title: "Single source KYC workflow",
    owner: "Kabelo",
    status: "wip",
    body:
      "KYC workflow consolidated in the peppermint_motherboard. Port to the Bvndle Command Centre underway so KYC becomes one workflow across all customer journeys (LaaS, card, app, partner onboarding).",
  },
];

// ---------------------------------------------------------------------------
// Roll-up helpers — single source of truth for the value-indicator numbers
// the chairman page renders per category and for the briefing summary.
// ---------------------------------------------------------------------------

export interface CategoryRollup {
  onboarded: number;
  pipeline: number;
  total: number;
  totalBranches: number;
  onboardedBranches: number;
  ytdRedemption: number;
}

export function rollup(cat: PartnerCategory): CategoryRollup {
  const r: CategoryRollup = {
    onboarded: 0,
    pipeline: 0,
    total: cat.merchants.length,
    totalBranches: 0,
    onboardedBranches: 0,
    ytdRedemption: 0,
  };
  for (const m of cat.merchants) {
    if (m.status === "onboarded") r.onboarded += 1;
    else r.pipeline += 1;
    if (typeof m.totalBranches === "number") r.totalBranches += m.totalBranches;
    if (typeof m.onboardedBranches === "number") r.onboardedBranches += m.onboardedBranches;
    if (typeof m.ytdRedemption === "number") r.ytdRedemption += m.ytdRedemption;
  }
  return r;
}

export interface BriefingSummary {
  categories: number;
  totalMerchants: number;
  totalOnboarded: number;
  totalPipeline: number;
  totalYtdRedemption: number;
  /** Top YTD redeemer name + coin count — the lede on the chairman page. */
  topRedeemer: { name: string; category: string; ytd: number } | null;
}

export function briefingSummary(
  cats: PartnerCategory[] = PARTNER_CATEGORIES,
): BriefingSummary {
  let totalMerchants = 0;
  let totalOnboarded = 0;
  let totalPipeline = 0;
  let totalYtdRedemption = 0;
  let topRedeemer: BriefingSummary["topRedeemer"] = null;
  for (const cat of cats) {
    for (const m of cat.merchants) {
      totalMerchants += 1;
      if (m.status === "onboarded") totalOnboarded += 1;
      else totalPipeline += 1;
      const ytd = typeof m.ytdRedemption === "number" ? m.ytdRedemption : 0;
      totalYtdRedemption += ytd;
      if (ytd > (topRedeemer?.ytd ?? 0)) {
        topRedeemer = { name: m.name, category: cat.label, ytd };
      }
    }
  }
  return {
    categories: cats.length,
    totalMerchants,
    totalOnboarded,
    totalPipeline,
    totalYtdRedemption,
    topRedeemer,
  };
}
